import { SubmitButton, Textarea } from "@xola/ui-kit";
import {
    FormField,
    InputField,
    nullableEmail,
    nullableString,
    requiredEmail,
    requiredString,
    requiredStringButNullable,
    SelectField,
    SwitchField,
    urlWithOptionalProtocol,
} from "common";
import { FormikHelpers } from "formik";
import { isEmpty } from "lodash";
import React from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { Affiliate, AffiliateStatus, User } from "types";
import { boolean, object, string } from "yup";
import { api } from "../../api/api";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useSeller, useUser } from "../../hooks/useAccount";
import { useAutoFetchCollection } from "../../hooks/useAutoFetchCollection";
import { isAffiliate } from "../../models/User";
import { updateCache } from "../../utils/updateCache";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { ContentLayout } from "../Layout/ContentLayout";

const getValidationSchema = (user: User, agent?: Affiliate) => {
    return object().shape({
        parent: string(),
        name: requiredStringButNullable(),
        email: agent?.user
            ? requiredEmail()
            : string().when("delegated", {
                  is: true,
                  then: requiredEmail(),
                  otherwise: nullableEmail(),
              }),
        phone: nullableString(),
        url: urlWithOptionalProtocol(),
        address: nullableString(),
        notes: nullableString(),
        code: isAffiliate(user) ? nullableString() : requiredString(),
        delegated: boolean().required(),
    });
};

const initialValues: Affiliate = {
    id: "",
    parent: "",
    name: "",
    email: "",
    phone: "",
    url: "",
    address: "",
    notes: "",
    code: "",
    delegated: false,
    status: AffiliateStatus.ACTIVE,
};

const getInitialValues = (agent?: Affiliate) => {
    if (!agent) {
        return initialValues;
    }

    return {
        ...initialValues,
        ...agent,
    };
};

interface EditAgentFormProps {
    agent?: Affiliate;
    onSubmit: (agent: Affiliate) => void;
}

const shouldShowGrantAccessToggle = (
    isAffiliateLoginEnabled: boolean,
    user: User,
    userAgent?: Affiliate,
    agent?: Affiliate,
) => {
    if (!isAffiliateLoginEnabled) {
        return false;
    }

    return isAffiliate(user)
        ? user && userAgent?.id !== agent?.id && userAgent?.allowChildren && userAgent.id === agent?.parent
        : true;
};

const shouldShowAllowChildrenToggle = (isAffiliateLoginEnabled: boolean, user: User, agent?: Affiliate) => {
    if (!isAffiliateLoginEnabled) {
        return false;
    }

    return isAffiliate(user) ? isAffiliateLoginEnabled && agent?.user?.id !== user.id && !agent?.parent && agent : true;
};

export const EditAgentForm = ({ agent, onSubmit }: EditAgentFormProps) => {
    const user = useUser();
    const seller = useSeller();
    const history = useHistory();
    const isAffiliateLoginEnabled = seller.preferences.affiliate?.allowLogin ?? false;
    const { data: agents, refetch } = useAutoFetchCollection<Affiliate>(["affiliates"]);

    const parentAgents = agents.filter((agent) => {
        return agent.status === AffiliateStatus.ACTIVE && !agent.parent;
    });

    const agentForCurrentUser = agents.find((agentObject) => agentObject.user?.id === user.id);
    const getAgentCode = (agent: Affiliate) => (agent.code ? `${agent.code.toUpperCase()}: ` : "");

    const { mutateAsync: saveAgent, isLoading } = useMutation(async (payload: Affiliate) => {
        if (agent?.id) {
            return api.put<Affiliate>(`/affiliates/${agent.id}`, payload);
        }

        return api.post<Affiliate>(`/affiliates`, { ...payload, seller: { id: seller.id } });
    });

    const handleCancelClick = () => {
        history.push(`/distribution/agents`);
    };

    const handleFormSubmit = async (values: Affiliate, { setFieldError }: FormikHelpers<Affiliate>) => {
        try {
            const payload = { ...values };
            payload.email = payload.email ? payload.email.toLowerCase().trim() : null;

            if (values.parent) {
                payload.allowChildren = false;
            }

            const { data: agent } = await saveAgent(payload);
            updateCache("affiliates", agent); // Update it in cache and invalidate
            await refetch(); // Wait for this to finish otherwise a page navigation causes the fetch to abort
            onSubmit(agent);
        } catch (error: any) {
            if (error?.response?.data?.field?.email) {
                setFieldError("email", "- Agent with this email already exists");
            } else if (error?.response?.data?.field?.code) {
                if (error?.response?.data?.field?.code?.reason === "already_exists") {
                    setFieldError("code", `- ${error?.response?.data?.field?.code?.message ?? "Code already in use"}`);
                } else {
                    setFieldError("code", "- Agent with this code should not contain spaces, quotes, <, >, &");
                }
            } else if (
                error?.response?.data?.field?.delegated &&
                error?.response?.data?.field?.delegated.reason === "not_allowed"
            ) {
                setFieldError("delegated", "- Not authorized to change the login access for this agent");
            }
        }
    };

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={getValidationSchema(user, agent)}
            initialValues={getInitialValues(agent)}
            onSubmit={handleFormSubmit}
        >
            {({ handleSubmit, dirty, values, setFieldValue }: any) => (
                <ContentLayout isWide className="space-y-12" isDisabled={isLoading}>
                    <h2>Agent Profile</h2>

                    <div className="flex space-x-2">
                        <div className="flex max-w-[260px] flex-col">
                            <h3>Agent Profile</h3>
                            <p className="text-gray-darker">Create and update Agent information</p>
                        </div>
                        <div className="w-1/2 pl-8">
                            <div className="space-y-4">
                                {isAffiliate(user) ? null : (
                                    <FormField name="parent" label="Parent Agent">
                                        <SelectField className="no-translate">
                                            <option defaultChecked value="">
                                                None
                                            </option>

                                            {parentAgents
                                                .filter((parentAgent) => parentAgent.id !== agent?.id)
                                                .map((parentAgent) => (
                                                    <option key={parentAgent.id} value={parentAgent.id}>
                                                        {getAgentCode(parentAgent)} {parentAgent.name}
                                                    </option>
                                                ))}
                                        </SelectField>
                                    </FormField>
                                )}

                                {shouldShowAllowChildrenToggle(isAffiliateLoginEnabled, user, agent) &&
                                isEmpty(values.parent) ? (
                                    <FormField name="allowChildren" label={null}>
                                        <SwitchField
                                            isChecked={values.allowChildren}
                                            onChange={(value: boolean) => setFieldValue("allowChildren", value)}
                                        >
                                            Allow this agent to create child agents
                                        </SwitchField>
                                    </FormField>
                                ) : null}

                                <FormField name="name" label="Name">
                                    <InputField isRequired placeholder="Name" />
                                </FormField>

                                {shouldShowGrantAccessToggle(
                                    isAffiliateLoginEnabled,
                                    user,
                                    agentForCurrentUser,
                                    agent,
                                ) ? (
                                    <FormField name="delegated" label={null}>
                                        <SwitchField
                                            isChecked={values.delegated}
                                            onChange={(value: boolean) => setFieldValue("delegated", value)}
                                        >
                                            Allowing agent user login
                                        </SwitchField>
                                    </FormField>
                                ) : null}

                                <FormField name="email" label="Email">
                                    <InputField placeholder="Email" />
                                </FormField>

                                <FormField name="phone" label="Phone">
                                    <InputField placeholder="Phone" />
                                </FormField>

                                <FormField name="url" label="URL">
                                    <InputField placeholder="URL" />
                                </FormField>

                                <FormField name="address" label="Address">
                                    <InputField placeholder="Address" />
                                </FormField>

                                <FormField name="notes" label="Notes">
                                    <InputField as={Textarea} placeholder="Notes" />
                                </FormField>

                                {!isAffiliate(user) && (
                                    <FormField name="code" label="Code">
                                        <InputField isRequired placeholder="Code" />
                                    </FormField>
                                )}
                            </div>
                        </div>
                    </div>

                    <ContentLayout.Footer>
                        <SubmitButton type="submit" disabled={!dirty} isLoading={isLoading} onClick={handleSubmit}>
                            {agent || isAffiliate(user) ? "Save" : "Next"}
                        </SubmitButton>

                        <SecondaryButton type="reset" className="ml-3" onClick={handleCancelClick}>
                            Cancel
                        </SecondaryButton>
                    </ContentLayout.Footer>

                    <ScrollToFieldError />
                </ContentLayout>
            )}
        </XolaFormik>
    );
};
